import { useState, useEffect } from "react";
import RoleRefiner from "./RoleRefiner";
import Refine from "./Refine";
import { resolveWithContext } from "../utils/contextResolver";
import { loadResolver } from "../lib/taxonomy";
import { useAuth } from "../lib/auth";
import SignIn from "./SignIn";
import { resolveWithModel } from "../utils/modelResolve";
import { PANEL, TEAL, CREAM, CREAM60, CREAM30, BORDER, AMBER, GREEN, NAVY } from "../utils/constants";

/**
 * Step 2: what we found, and what we still need to ask.
 *
 * Two principles are visible here rather than implied:
 *
 * 1. ADR-021 — the match is shown WITH its confidence and how it was reached.
 *    Derived and inspectable, never a verdict.
 * 2. ADR-025 — questions appear only for declared gaps. A question not driven
 *    by a gap is a bug, and the question count is the AI-first scoreboard.
 */

const QUESTIONS = {
  "current-role": {
    label: "Which of these is closest to your role?",
    why: "We couldn't pin this down from your profile with enough confidence to build on.",
  },
  skills: {
    label: "What are you known for at work?",
    why: "Your profile didn't list skills we could read.",
  },
  tenure: {
    label: "Roughly how long have you been in this field?",
    why: "We couldn't find dates to work from.",
  },
  "what-you-actually-do": {
    label: "In a normal week, what takes most of your time?",
    why: "Job titles hide more than they reveal. This is what makes the analysis yours rather than your job title's.",
  },
};

function Row({ label, value, tone = CREAM }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "8px 0",
                  borderBottom: `1px solid ${BORDER}` }}>
      <span style={{ color: CREAM60, fontSize: 13 }}>{label}</span>
      <span style={{ color: tone, fontSize: 13, fontWeight: 600, textAlign: "right" }}>{value}</span>
    </div>
  );
}

export default function Assessment({ parsed, onConfirm, onRestart }) {
  const [resolution, setResolution] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [answers, setAnswers] = useState({});
  const [dwaIndex, setDwaIndex] = useState(null);
  // The refinement result, kept here so it survives to onConfirm. `answers`
  // above is the free-text gap form and is a different thing entirely — reading
  // activity confirmations out of it silently produced an empty list.
  const [refined, setRefined] = useState(null);
  const [context, setContext] = useState("");
  const [refining, setRefining] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [modelResult, setModelResult] = useState(null);
  const [modelError, setModelError] = useState(null);
  const { user } = useAuth();
  const years = parsed.tenureMonths ? Math.round(parsed.tenureMonths / 12) : null;

  // Context-aware resolution: titles say what someone is CALLED, descriptions
  // and history say what they DO. A rank-only title like "Associate Director"
  // resolved on its own produced "Brokerage Clerks" for a 22-year technology
  // delivery leader — see contextResolver.js.
  const run = async (extra) => {
    setRefining(true);
    const [resolver, tasksMod, dwaMod] = await Promise.all([
      loadResolver(),
      import("../data/taxonomy/tasks.json"),
      import("../data/taxonomy/dwas.json"),
    ]);
    // Two channels, deliberately. Task text names the client; Detailed Work
    // Activities name only the work (ADR-032).
    setDwaIndex(dwaMod.default);
    const r = resolveWithContext(parsed, resolver, tasksMod.default, extra || "", dwaMod.default);
    setResolution(r);
    setChosen((c) => c || r.matches[0]?.code || null);
    setRefining(false);

    // Local resolution has a ceiling (contextResolver.js). When it is uncertain
    // and the person is signed in, escalate to the model.
    //
    // When they are NOT signed in, do nothing. An earlier version raised a
    // sign-in card here, which put a second wall in front of the diagnosis —
    // and because almost every senior or ambiguously-titled profile escalates,
    // almost everyone would have met it before seeing a single number. That is
    // precisely the wall ADR-036 moved: the diagnosis is free and ungated, the
    // account wall stands at the pathway. Anonymous visitors keep the
    // deterministic ranking and can correct it by hand in the refiner below,
    // which is a real answer rather than a locked door.
    if (r.confidence < 0.7 && r.matches.length > 1 && user) {
      escalate(r, extra || "");
    }
  };

  const escalate = async (r, extra) => {
    setRefining(true); setModelError(null);
    try {
      const m = await resolveWithModel(parsed, r.matches, extra);
      setModelResult(m);
      setChosen(m.code);
    } catch (e) {
      // A failed model call is not an error state — local resolution stands and
      // the user can still correct it by hand (ADR-002).
      setModelError(e.status === 429 ? "We've hit today's analysis limit — the ranking below is still yours to correct." : null);
    } finally {
      setRefining(false);
    }
  };
  useEffect(() => { run(""); }, [parsed]);

  const matches = resolution?.matches || [];
  const role = matches.find((m) => m.code === chosen) || matches[0] || null;

  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ color: CREAM, fontSize: 22, margin: "0 0 4px", fontFamily: "'Space Grotesk', sans-serif" }}>
          What we found
        </h2>
        <p style={{ color: CREAM60, fontSize: 14, margin: 0 }}>
          Read from your profile on this device. Correct anything that's wrong.
        </p>
      </div>

      <div style={{ background: PANEL, border: `1px solid ${BORDER}`, borderTop: `2px solid ${TEAL}`,
                    borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: TEAL, fontWeight: 600, marginBottom: 12, textTransform: "uppercase",
                      letterSpacing: 1, fontFamily: "'JetBrains Mono', monospace" }}>
          Your current role
        </div>
        {role ? (
          <>
            <div style={{ fontSize: 18, fontWeight: 700, color: CREAM, marginBottom: 4 }}>{role.title}</div>
            <div style={{ fontSize: 12, color: CREAM30, fontFamily: "'JetBrains Mono', monospace" }}>
              from “{parsed.roles[0]?.title || "your profile"}” · {Math.round((resolution?.confidence || 0) * 100)}% ·{" "}
              {resolution?.via || "…"}
            </div>
          </>
        ) : (
          <div style={{ color: CREAM60, fontSize: 14 }}>Reading your profile…</div>
        )}
      </div>

      {/* An OFFER, never a gate. Model-assisted matching is signed-in only because
          it spends money on a public endpoint (ADR-023) — but the deterministic
          ranking above is already usable, and the refiner below already lets
          anyone correct it. */}
      {!user && resolution && resolution.confidence < 0.7 && !showSignIn && (
        <button onClick={() => setShowSignIn(true)}
          style={{ width: "100%", textAlign: "left", background: "transparent",
                   border: `1px dashed ${CREAM30}`, borderRadius: 10, padding: "12px 16px",
                   marginBottom: 16, cursor: "pointer", color: CREAM60, fontSize: 12.5,
                   lineHeight: 1.55, fontFamily: "'Space Grotesk', sans-serif" }}>
          Signed-in visitors get a second opinion from the model on matches like this one.
          <span style={{ color: TEAL }}> Sign in →</span> Or just pick the right one below.
        </button>
      )}

      {showSignIn && !user && (
        <SignIn
          reason="Let's get your role right"
          onDone={() => { setShowSignIn(false); if (resolution) escalate(resolution, context); }}
          onSkip={() => setShowSignIn(false)}
        />
      )}

      {modelResult && (
        <div style={{ background: `${GREEN}10`, border: `1px solid ${GREEN}40`, borderRadius: 12,
                      padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: GREEN, fontWeight: 700, marginBottom: 4,
                        textTransform: "uppercase", letterSpacing: 1,
                        fontFamily: "'JetBrains Mono', monospace" }}>Refined match</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: CREAM }}>{modelResult.title}</div>
          <div style={{ fontSize: 12, color: CREAM60, marginTop: 4, lineHeight: 1.5 }}>{modelResult.why}</div>
        </div>
      )}

      {modelError && (
        <div style={{ background: `${AMBER}10`, border: `1px solid ${AMBER}30`, borderRadius: 8,
                      padding: "8px 14px", marginBottom: 14, fontSize: 12, color: AMBER }}>{modelError}</div>
      )}

      {/* Free for everyone (ADR-033). It costs nothing to run and it is the only
          step where the person tells us something the résumé never said — so
          gating it would make the screen most people see the weakest one. */}
      {matches.length > 1 && dwaIndex && (
        <Refine
          matches={matches}
          dwaIndex={dwaIndex}
          parsed={parsed}
          // Deliberately does NOT write back into `resolution`. `matches` comes
          // from there and is the prop Refine watches, so feeding the re-ranked
          // list back re-ran its effect, rebuilt the questions from the new
          // ranking, and reset the panel — a loop where the question count
          // changed on every pass. The refinement lives in its own state and
          // `resolution` stays the "before" that movement is measured against.
          onApplied={(activityAnswers, ranked) => {
            setRefined({ activityAnswers, ranked });
            setChosen((c) => c || ranked[0]?.code || null);
          }}
          onResolved={(code) => setChosen(code)}
        />
      )}

      {matches.length > 0 && (
        <RoleRefiner
          matches={refined?.ranked || matches}
          via={resolution?.via}
          confidence={resolution?.confidence || 0}
          chosen={chosen}
          onChoose={setChosen}
          refining={refining}
          onRefine={(t) => { setContext(t); setModelResult(null); run(`${context} ${t}`); }}
        />
      )}

      <div style={{ background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 12,
                    padding: 20, marginBottom: 16 }}>
        <Row label="Roles found in your history" value={parsed.roles.length} />
        <Row label="Skills read from your profile" value={parsed.skills.length || "none found"}
             tone={parsed.skills.length ? CREAM : AMBER} />
        {years !== null && <Row label="Experience" value={`${years} year${years === 1 ? "" : "s"}`} />}
        <Row label="Source" value={parsed.source} tone={CREAM30} />
      </div>

      {parsed.skills.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          {parsed.skills.slice(0, 12).map((s, i) => (
            <span key={i} style={{ background: `${TEAL}15`, border: `1px solid ${TEAL}40`, borderRadius: 8,
                                   padding: "6px 12px", fontSize: 13, color: CREAM }}>{s}</span>
          ))}
        </div>
      )}

      {parsed.gaps.filter((g) => g !== "current-role").map((g) => (
        <div key={g} style={{ background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 12,
                              padding: 20, marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: CREAM, marginBottom: 4 }}>
            {QUESTIONS[g]?.label || g}
          </div>
          <div style={{ fontSize: 12, color: CREAM30, marginBottom: 10 }}>{QUESTIONS[g]?.why}</div>
          <textarea
            rows={2} value={answers[g] || ""}
            onChange={(e) => setAnswers({ ...answers, [g]: e.target.value })}
            style={{ width: "100%", background: NAVY, border: `1px solid ${CREAM30}`, borderRadius: 8,
                     padding: "10px 12px", color: CREAM, fontSize: 13, outline: "none",
                     fontFamily: "'Space Grotesk', sans-serif", resize: "vertical" }}
          />
        </div>
      ))}

      <div style={{ fontSize: 11, color: CREAM30, marginBottom: 16,
                    fontFamily: "'JetBrains Mono', monospace" }}>
        {parsed.gaps.length === 0
          ? "Nothing to ask — we read everything we needed."
          : `${parsed.gaps.length} question${parsed.gaps.length === 1 ? "" : "s"} — everything else came from your profile.`}
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button onClick={() => onConfirm({ ...parsed, chosenCode: chosen, chosenTitle: role?.title, answers, context,
                                   confidence: refined
                                     ? (refined.ranked.find((x) => x.code === chosen)?.confidence
                                        ?? refined.ranked[0]?.confidence ?? null)
                                     : (resolution?.confidence ?? null),
                                   resolvedVia: resolution?.via ?? null,
                                   // From the refinement, not from the free-text gap form.
                                   confirmedActivities: Object.entries(refined?.activityAnswers || {})
                                     .filter(([, v]) => v === true).map(([k]) => k) })}
          disabled={!chosen}
          style={{ flex: 1, minWidth: 200, background: `linear-gradient(135deg, ${TEAL}, #00A3A9)`,
                   color: NAVY, border: "none", padding: "14px 28px", borderRadius: 8, cursor: "pointer",
                   fontSize: 15, fontWeight: 700 }}>
          Continue →
        </button>
        <button onClick={onRestart}
          style={{ background: "none", border: `1px solid ${CREAM30}`, color: CREAM60,
                   padding: "14px 20px", borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
          Use a different file
        </button>
      </div>
    </div>
  );
}
